import type { AccessSourceType, PermissionUpdateEntry } from '@thinkalpha/common/contracts/permissions.js';
import type {
    AddPoolItemsRequest,
    AddPoolItemsResponse,
    CreatePoolRequest,
    DeletePoolRequest,
    DeletePoolResponse,
    GetPoolChangeLogResponse,
    ListPoolItemsResponse,
    RemovePoolItemResponse,
    UpdatePoolItemRequest,
    UpdatePoolItemResponse,
} from '@thinkalpha/locates-ws-client';
import type {
    BorrowStatusKey,
    PoolPricingSimulationForPoolRequest,
    PoolPricingSimulationRequest,
    PoolPricingSimulationResponse,
    SymbolPricesResponse,
} from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';
import type {
    CreatePoolResponse,
    GetPoolResponse,
    ListPoolsResponse,
    UpdatePoolContractRequest,
    UpdatePoolResponse,
} from '@thinkalpha/ui-types/types/admin/lab-pools';
import { lapi as axios } from 'src/api';

/**
 * HTTP wrappers around the locates-ws `Inventory (Modern)` endpoints.
 * Read code lives alongside writes here, but the React-Query factory
 * files in {@link ../labPools} and {@link ../labPoolsMutations} split
 * read vs. write so callers can stay on the side they need.
 *
 * Backend swagger:
 *   GET    /v1/inventory/pools                       — ListPools
 *   GET    /v1/inventory/pools?firmId=<uuid>         — ListPools (firm-scoped)
 *   GET    /v1/inventory/pools/{poolId}                — GetPool (Inspect a pool)
 *   DELETE /v1/inventory/pools/{poolId}                — DeletePool (modern pools,
 *                                                     reason ≥ 20 chars in body)
 *   GET    /v1/inventory/pools/{poolId}/items        — ListPoolItems
 *   GET    /v1/inventory/pools/{poolId}/items?symbol=… — ListPoolItems (symbol-filtered)
 *   POST   /v1/inventory/pools/{poolId}/items        — AddPoolItems (async, batched 50)
 *   PATCH  /v1/inventory/pools/{poolId}/items/{id}   — UpdatePoolItem
 *                                                     NB: price change replaces
 *                                                     the row — response.item.id
 *                                                     may differ from the URL id
 *   DELETE /v1/inventory/pools/{poolId}/items/{id}   — RemovePoolItem (501 today;
 *                                                     unblocks when backend ships
 *                                                     the SQL procedure)
 *
 * The `AddPoolItems` upload is acknowledged synchronously with
 * `{ uploadId, accepted }`; the items themselves arrive over the
 * `v1.inventory.pools.item` WebSocket stream once the client has
 * joined the pool room. The WS integration is not wired yet; the
 * model relies on `invalidatePoolItemsQuery` to refetch after writes
 * settle.
 */

export const getAllPools = (): Promise<ListPoolsResponse> => {
    return axios.get<ListPoolsResponse>('/v1/inventory/pools').then((x) => x.data);
};

export const getFirmPools = (firmId: string): Promise<ListPoolsResponse> => {
    return axios.get<ListPoolsResponse>('/v1/inventory/pools', { params: { firmId } }).then((x) => x.data);
};

/**
 * Create a new modern inventory pool. The backend requires `owner`
 * access on the target firm (superusers may create for any firm); the
 * created pool is always modern (`isModern: true`) and starts with no
 * per-entity permissions, relying on firm-level access for visibility.
 */
export const createPool = (request: CreatePoolRequest): Promise<CreatePoolResponse> => {
    return axios.post<CreatePoolResponse>('/v1/inventory/pools', request).then((x) => x.data);
};

/**
 * Inspect a single pool by id. Returns the same `InventoryPool`
 * shape that appears in `ListPoolsResponse.pools`, useful for
 * deep-link entry points that need the pool metadata before (or
 * without) loading the full pool list.
 */
export const getPool = (poolId: string): Promise<GetPoolResponse> => {
    return axios.get<GetPoolResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}`).then((x) => x.data);
};

export interface GetPoolItemsOptions {
    /**
     * Exact-match ticker filter. The backend compares `sym = @sym` with
     * no case normalization, so this is case-sensitive against the
     * upper-case stored symbols — callers should upper-case the term.
     * When set, the backend returns every matching item and ignores
     * `skip` / `limit`.
     */
    symbol?: string;
    /** Zero-based starting row. Ignored when `symbol` is set. */
    skip?: number;
    /** Page size. Ignored when `symbol` is set. */
    limit?: number;
}

/**
 * List a pool's items, optionally paged. Returns `{ items, count }`
 * where `count` is the total matching rows in the pool (so the client
 * can paginate without a second `HEAD`-style call). When `symbol` is
 * supplied, paging is bypassed by the backend.
 */
export const getPoolItems = (poolId: string, options?: GetPoolItemsOptions): Promise<ListPoolItemsResponse> => {
    const params: Record<string, string | number> = {};
    if (options?.symbol) params.symbol = options.symbol;
    if (options?.skip !== undefined) params.skip = options.skip;
    if (options?.limit !== undefined) params.limit = options.limit;
    return axios
        .get<ListPoolItemsResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}/items`, {
            params: Object.keys(params).length ? params : undefined,
        })
        .then((x) => x.data);
};

/**
 * Page size used when gathering an entire pool by real backend
 * pagination (the "all rows" table view and the CSV export). Matches
 * the endpoint's default `limit`.
 */
export const POOL_ITEMS_PAGE_SIZE = 500;

/**
 * Upper bound on in-flight page requests when gathering an entire pool.
 * Under HTTP/2 a single connection multiplexes requests with no per-host
 * socket cap, so a large pool would otherwise fire hundreds of requests
 * at once. Batching caps the burst to keep memory and backend load bounded.
 */
export const POOL_ITEMS_MAX_CONCURRENT_PAGES = 8;

/**
 * Fetches every item in a pool by paging the backend with real
 * `skip`/`limit` requests until the reported `count` is reached, rather
 * than guessing a single oversized `limit`. The first page reports the
 * total; the remaining pages are fetched in bounded concurrent batches
 * and concatenated in order.
 */
export const getAllPoolItems = async (poolId: string): Promise<ListPoolItemsResponse> => {
    const first = await getPoolItems(poolId, { skip: 0, limit: POOL_ITEMS_PAGE_SIZE });
    const { count } = first;
    const totalPages = Math.ceil(count / POOL_ITEMS_PAGE_SIZE);
    if (totalPages <= 1) return { items: first.items, count };
    const rest: ListPoolItemsResponse[] = [];
    for (let page = 1; page < totalPages; page += POOL_ITEMS_MAX_CONCURRENT_PAGES) {
        const batchSize = Math.min(POOL_ITEMS_MAX_CONCURRENT_PAGES, totalPages - page);
        const batch = await Promise.all(
            Array.from({ length: batchSize }, (_, i) =>
                getPoolItems(poolId, { skip: (page + i) * POOL_ITEMS_PAGE_SIZE, limit: POOL_ITEMS_PAGE_SIZE }),
            ),
        );
        rest.push(...batch);
    }
    return { items: [first.items, ...rest.map((page) => page.items)].flat(), count };
};

export interface GetPoolChangeLogOptions {
    skip?: number;
    limit?: number;
    /**
     * OData `$filter` expression sent as a GET query param — the symbol,
     * change-kind, and time-range filters are combined here so the backend
     * filters and paginates the log together. It is a query param, not part
     * of the typed client contract. The backend validates the expression
     * against its supported clause grammar (`timestamp ge/gt/le/lt '<quoted
     * date>'`, `symbol eq '<symbol>'`, and `kind eq '<InventoryChangeKind>'`,
     * joined with `and`) and ignores the whole expression, logging a warning,
     * on anything else.
     */
    filter?: string;
}

/**
 * Page through a pool's audit log (sourced from the legacy SQL
 * Journal). Returns `{ entries, count }`. The backend's note flags
 * the contract as "untested, subject to change", so do not consume
 * the raw `action` integer without a fallback for unknown codes.
 */
export const getPoolChangeLog = (
    poolId: string,
    options?: GetPoolChangeLogOptions,
): Promise<GetPoolChangeLogResponse> => {
    const params: Record<string, string | number> = {};
    if (options?.skip !== undefined) params.skip = options.skip;
    if (options?.limit !== undefined) params.limit = options.limit;
    if (options?.filter) params.$filter = options.filter;
    return axios
        .get<GetPoolChangeLogResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}/log`, {
            params: Object.keys(params).length ? params : undefined,
        })
        .then((x) => x.data);
};

export const addPoolItems = ({
    poolId,
    request,
}: {
    poolId: string;
    request: AddPoolItemsRequest;
}): Promise<AddPoolItemsResponse> => {
    return axios
        .post<AddPoolItemsResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}/items`, request)
        .then((x) => x.data);
};

/**
 * Update an item's `quantity` and/or `price`.
 *
 * IMPORTANT: a price change is implemented server-side as a row
 * replacement, not an in-place update. When the request body includes
 * `price`, `response.item.id` will differ from the `{itemId}` passed
 * in the URL. Callers that maintain a local id-keyed cache must reconcile
 * — either by patching the cache with the new `item.id` directly, or by
 * invalidating the items query so the refetch picks up the new id. The
 * model takes the invalidation path; if/when we add optimistic updates,
 * use `response.item` rather than re-keying by the URL id.
 */
export const updatePoolItem = ({
    poolId,
    itemId,
    request,
}: {
    poolId: string;
    itemId: string;
    request: UpdatePoolItemRequest;
}): Promise<UpdatePoolItemResponse> => {
    return axios
        .patch<UpdatePoolItemResponse>(
            `/v1/inventory/pools/${encodeURIComponent(poolId)}/items/${encodeURIComponent(itemId)}`,
            request,
        )
        .then((x) => x.data);
};

/**
 * Remove an item by id. Currently returns `501 Not Implemented` —
 * backend will ship the SQL procedure later. The trash-can affordance
 * is already wired through `submitChanges` so it will begin working
 * automatically without a UI change.
 */
export const removePoolItem = ({
    poolId,
    itemId,
}: {
    poolId: string;
    itemId: string;
}): Promise<RemovePoolItemResponse> => {
    return axios
        .delete<RemovePoolItemResponse>(
            `/v1/inventory/pools/${encodeURIComponent(poolId)}/items/${encodeURIComponent(itemId)}`,
        )
        .then((x) => x.data);
};

/**
 * Update a pool's mutable fields — `active` and the pool's `pricing`
 * config; omitted fields are left unchanged (requires `owner`). Returns
 * the updated pool — model code should reconcile
 * its cache against `response.pool` rather than the request value, in case
 * the backend reshapes anything else server-side.
 */
export const updatePool = ({
    poolId,
    request,
}: {
    poolId: string;
    request: UpdatePoolContractRequest;
}): Promise<UpdatePoolResponse> => {
    return axios
        .patch<UpdatePoolResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}`, request)
        .then((x) => x.data);
};

/**
 * Delete a modern inventory pool. Hits `DELETE /v1/inventory/pools/{poolId}`
 * with a body carrying a human-readable `reason` (backend rejects fewer
 * than 20 characters after trimming). Restricted to superusers and
 * owners of the pool's firm; legacy (non-modern) pools are rejected
 * with a 400. The backend emails the pool's notification address with
 * the reason before removing the pool, and the removal is permanent.
 */
export const deletePool = ({
    poolId,
    request,
}: {
    poolId: string;
    request: DeletePoolRequest;
}): Promise<DeletePoolResponse> => {
    return axios
        .delete<DeletePoolResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}`, { data: request })
        .then((x) => x.data);
};

/**
 * Replace the set of permission entries that `entityType`/`entityId`
 * holds on `poolId`. Send `entries` to grant the listed accesses (each
 * entry has its own `kind: 'allow' | 'deny'`); send an empty array to
 * revoke. Backend requires `owner` permission on the pool. Returns
 * 204; the model should refetch the pool after the call settles to
 * pick up the new `permissions` payload.
 */
export const updatePoolPermissionsForEntity = ({
    poolId,
    entityType,
    entityId,
    entries,
}: {
    poolId: string;
    entityType: AccessSourceType;
    entityId: string;
    entries: PermissionUpdateEntry[];
}): Promise<void> => {
    return axios
        .put<void>(
            `/v1/inventory/pools/${encodeURIComponent(poolId)}/permissions/${encodeURIComponent(
                entityType,
            )}/${encodeURIComponent(entityId)}`,
            entries,
        )
        .then(() => undefined);
};

/**
 * The price a pool would offer one of its own positions at: the item
 * supplies the symbol and stored price, the pool supplies the saved
 * pricing config, and `borrowStatus` selects the configured price floor
 * (no floor applies when omitted). Requires `reader` on the pool; the
 * backend returns 400 when the pool has no pricing configured.
 */
/**
 * Simulate a pool price against an arbitrary config via
 * `POST /v1/inventory/pricing` (superuser-only — not scoped to a pool the
 * caller can access). Backs the Info tab simulator's draft previews.
 */
export const simulatePricing = (request: PoolPricingSimulationRequest): Promise<PoolPricingSimulationResponse> => {
    return axios.post<PoolPricingSimulationResponse>('/v1/inventory/pricing', request).then((x) => x.data);
};

/**
 * Simulate a pool price against the pool's SAVED pricing config via
 * `POST /v1/inventory/pools/{poolId}/pricing`. Requires `reader` on the
 * pool; the backend returns 400 when the pool has no pricing configured.
 */
export const simulatePoolPricing = (
    poolId: string,
    request: PoolPricingSimulationForPoolRequest,
): Promise<PoolPricingSimulationResponse> => {
    return axios
        .post<PoolPricingSimulationResponse>(`/v1/inventory/pools/${encodeURIComponent(poolId)}/pricing`, request)
        .then((x) => x.data);
};

export const getPoolItemPricing = (
    poolId: string,
    itemId: string,
    borrowStatus?: BorrowStatusKey,
): Promise<PoolPricingSimulationResponse> => {
    return axios
        .get<PoolPricingSimulationResponse>(
            `/v1/inventory/pools/${encodeURIComponent(poolId)}/items/${encodeURIComponent(itemId)}/pricing`,
            { params: borrowStatus ? { borrowStatus } : undefined },
        )
        .then((x) => x.data);
};

/**
 * Read the locate-price index for a symbol: every price level with its
 * contributing providers and quote timestamps via
 * `GET /v1/admin/prices/{symbol}`. Read-only and config-independent, unlike
 * the pool-pricing simulation above. Currently rejects non-superusers with
 * a 403, but access is being opened to firm admins and owners.
 */
export const getSymbolPrices = (symbol: string): Promise<SymbolPricesResponse> => {
    return axios.get<SymbolPricesResponse>(`/v1/admin/prices/${encodeURIComponent(symbol)}`).then((x) => x.data);
};
